import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import Layout from '../../components/Layout';
import { useParams, useNavigate } from 'react-router-dom';
import { authAxios } from '../../utils/api';
import { hasPermission } from '../../utils/permissions';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { User, Palette, Phone, Mail, Calendar, Shield, Pencil, Check, X, Bell, Clock, Globe, KeyRound } from 'lucide-react';
import EmailPreferences from '../../components/EmailPreferences';

export default function PlayerDetail() {
  const { user: currentUser, seasons, API, tenantContext } = useContext(AppContext);
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingColor, setEditingColor] = useState(false);
  const [newColor, setNewColor] = useState('#3b82f6');
  const [editingPhone, setEditingPhone] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState('');
  
  // Password reset state
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // SMS Settings state
  const [editingSmsSettings, setEditingSmsSettings] = useState(false);
  const [smsSettings, setSmsSettings] = useState({
    sms_enabled: false,
    timezone: '',
    quiet_hours_start: '20:00',
    quiet_hours_end: '07:00'
  });
  
  // Verbal consent state for SMS
  const [smsVerbalConsent, setSmsVerbalConsent] = useState(false);
  const [showSmsConsentDialog, setShowSmsConsentDialog] = useState(false);

  // Timezone options
  const TIMEZONE_OPTIONS = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Phoenix', label: 'Arizona (MST)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
    { value: 'UTC', label: 'UTC' },
  ];

  // Quiet hours options (hourly)
  const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => {
    const hour = i.toString().padStart(2, '0');
    const label = i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`;
    return { value: `${hour}:00`, label };
  });

  const PLAYER_COLORS = [
    { value: '#f59e0b', name: 'Amber' },
    { value: '#3b82f6', name: 'Blue' },
    { value: '#10b981', name: 'Emerald' },
    { value: '#ef4444', name: 'Red' },
    { value: '#8b5cf6', name: 'Purple' },
    { value: '#ec4899', name: 'Pink' },
    { value: '#14b8a6', name: 'Teal' },
    { value: '#f97316', name: 'Orange' },
    { value: '#06b6d4', name: 'Cyan' },
    { value: '#84cc16', name: 'Lime' },
    { value: '#a855f7', name: 'Violet' },
    { value: '#64748b', name: 'Slate' },
    { value: '#9333ea', name: 'Purple' },  // Additional purple shade
    { value: '#22c55e', name: 'Green' },   // Additional green shade
  ];
  
  // Helper function to get color name from hex value
  const getColorName = (hexColor) => {
    if (!hexColor) return 'Blue';
    const normalizedHex = hexColor.toLowerCase();
    const exactMatch = PLAYER_COLORS.find(c => c.value.toLowerCase() === normalizedHex);
    if (exactMatch) return exactMatch.name;
    
    // If no exact match, try to determine color family from hex
    const r = parseInt(normalizedHex.slice(1, 3), 16);
    const g = parseInt(normalizedHex.slice(3, 5), 16);
    const b = parseInt(normalizedHex.slice(5, 7), 16);
    
    // Determine dominant color
    if (r > g && r > b) {
      if (g > 100) return 'Orange';
      if (b > 100) return 'Pink';
      return 'Red';
    } else if (g > r && g > b) {
      if (b > 150) return 'Teal';
      if (r > 150) return 'Lime';
      return 'Green';
    } else if (b > r && b > g) {
      if (r > 150) return 'Purple';
      if (g > 150) return 'Cyan';
      return 'Blue';
    } else if (r > 200 && g > 200 && b > 200) {
      return 'White';
    } else if (r < 100 && g < 100 && b < 100) {
      return 'Dark';
    }
    return 'Custom';
  };
  
  // Dialog states
  const [roleChangeDialogOpen, setRoleChangeDialogOpen] = useState(false);
  const [newRoleToSet, setNewRoleToSet] = useState('');
  const [grantAllAccessDialogOpen, setGrantAllAccessDialogOpen] = useState(false);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);

  useEffect(() => {
    // Load user if current user has VIEW_PLAYERS permission
    if (hasPermission('VIEW_PLAYERS', tenantContext, currentUser)) {
      loadUser();
    }
  }, [currentUser, userId, tenantContext]);

  const loadUser = async () => {
    try {
      setLoading(true);
      // Try loading as user first
      try {
        const response = await authAxios.get(`${API}/users/${userId}`);
        setUser({ ...response.data, isInvite: false });
        setNewName(response.data.name || '');
        setNewColor(response.data.color || '#3b82f6');
        // Set SMS settings
        setSmsSettings({
          sms_enabled: response.data.sms_enabled || false,
          timezone: response.data.timezone || '',
          quiet_hours_start: response.data.quiet_hours_start || '20:00',
          quiet_hours_end: response.data.quiet_hours_end || '07:00'
        });
        setSmsVerbalConsent(response.data.sms_verbal_consent || false);
      } catch (userError) {
        // If not found as user, try as invite
        const invitesRes = await authAxios.get(`${API}/users/invites`);
        const invite = invitesRes.data.find(inv => inv.id === userId);
        if (invite) {
          setUser({ ...invite, isInvite: true });
          setNewName(invite.name || '');
          setNewColor(invite.color || '#3b82f6');
        } else {
          throw new Error('User or invite not found');
        }
      }
    } catch (error) {
      console.error('Failed to load user:', error);
      toast.error('Failed to load user or invite');
    } finally {
      setLoading(false);
    }
  };

  const updateName = async () => {
    if (!newName.trim()) return;
    
    try {
      if (user.isInvite) {
        await authAxios.patch(
          `${API}/users/invites/${userId}`,
          { name: newName }
        );
      } else {
        await authAxios.patch(
          `${API}/users/${userId}`,
          { name: newName }
        );
      }
      toast.success('Name updated!');
      setEditingName(false);
      loadUser();
    } catch (error) {
      console.error('Failed to update name:', error);
      toast.error('Failed to update name');
    }
  };

  const updateColor = async () => {
    if (!newColor.trim()) return;
    
    try {
      if (user.isInvite) {
        await authAxios.patch(
          `${API}/users/invites/${userId}`,
          { color: newColor }
        );
      } else {
        await authAxios.patch(
          `${API}/users/${userId}`,
          { color: newColor }
        );
      }
      toast.success('Color updated!');
      setEditingColor(false);
      loadUser();
    } catch (error) {
      console.error('Failed to update color:', error);
      toast.error('Failed to update color');
    }
  };

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const updatePhone = async () => {
    try {
      await authAxios.patch(
        `${API}/users/${userId}`,
        { phone_number: newPhone.trim() || null }
      );
      toast.success('Phone number updated!');
      setEditingPhone(false);
      loadUser();
    } catch (error) {
      console.error('Failed to update phone:', error);
      toast.error('Failed to update phone number');
    }
  };

  const updateSmsSettings = async () => {
    try {
      await authAxios.patch(
        `${API}/users/${userId}`,
        {
          sms_enabled: smsSettings.sms_enabled,
          timezone: smsSettings.timezone || null,
          quiet_hours_start: smsSettings.quiet_hours_start,
          quiet_hours_end: smsSettings.quiet_hours_end
        }
      );
      toast.success('SMS settings updated!');
      setEditingSmsSettings(false);
      loadUser();
    } catch (error) {
      console.error('Failed to update SMS settings:', error);
      toast.error('Failed to update SMS settings');
    }
  };

  const handleRoleChangeClick = (newRole) => {
    setNewRoleToSet(newRole);
    setRoleChangeDialogOpen(true);
  };

  const confirmRoleChange = async () => {
    if (!newRoleToSet) return;
    
    try {
      await authAxios.patch(
        `${API}/users/${userId}`,
        { role: newRoleToSet }
      );
      toast.success('Role updated!');
      setRoleChangeDialogOpen(false);
      loadUser();
    } catch (error) {
      console.error('Failed to update role:', error);
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const toggleSeason = async (seasonId) => {
    const hasAccess = user.season_ids?.includes(seasonId);
    const action = hasAccess ? 'remove' : 'add';
    
    try {
      await authAxios.post(
        `${API}/users/${userId}/seasons`,
        { season_ids: [seasonId], action }
      );
      toast.success(hasAccess ? 'Season removed' : 'Season added');
      loadUser();
    } catch (error) {
      console.error('Failed to update seasons:', error);
      toast.error('Failed to update seasons');
    }
  };

  const handleGrantAllAccessClick = () => {
    setGrantAllAccessDialogOpen(true);
  };

  const confirmGrantAllAccess = async () => {
    try {
      await authAxios.post(
        `${API}/users/${userId}/seasons`,
        { season_ids: seasons.map(s => s.id), action: 'add' }
      );
      toast.success('All seasons added!');
      setGrantAllAccessDialogOpen(false);
      loadUser();
    } catch (error) {
      console.error('Failed to add all seasons:', error);
      toast.error('Failed to add all seasons');
    }
  };

  const deactivateUser = async () => {
    try {
      await authAxios.patch(
        `${API}/users/${userId}`,
        { status: 'deactivated', deactivated_reason: deactivateReason }
      );
      toast.success('User deactivated');
      setShowDeactivate(false);
      loadUser();
    } catch (error) {
      console.error('Failed to deactivate:', error);
      toast.error('Failed to deactivate user');
    }
  };

  const handleReactivateClick = () => {
    setReactivateDialogOpen(true);
  };

  const confirmReactivate = async () => {
    try {
      await authAxios.patch(
        `${API}/users/${userId}`,
        { status: 'active' }
      );
      toast.success('User reactivated!');
      setReactivateDialogOpen(false);
      loadUser();
    } catch (error) {
      console.error('Failed to reactivate:', error);
      toast.error('Failed to reactivate user');
      setReactivateDialogOpen(false);
    }
  };

  const resetPassword = async () => {
    if (!newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    try {
      setResettingPassword(true);
      await authAxios.post(
        `${API}/admin/users/${userId}/reset-password`,
        { new_password: newPassword }
      );
      toast.success('Password updated successfully');
      setShowResetPassword(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getActionDescription = (actionType) => {
    const descriptions = {
      invite_sent: 'Invitation sent',
      invite_accepted: 'Accepted invitation',
      user_updated: 'Profile updated',
      seasons_added: 'Seasons access added',
      seasons_removed: 'Seasons access removed'
    };
    return descriptions[actionType] || actionType;
  };

  // Permission check - League Admins can manage players
  const canViewPlayers = hasPermission('VIEW_PLAYERS', tenantContext, currentUser);

  if (!canViewPlayers) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-600">You need admin privileges to access this page.</p>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">User Not Found</h2>
          <Button onClick={() => navigate('/admin/players')}>Back to Players</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <Button variant="outline" onClick={() => navigate('/admin/players')} className="mb-6">
          ← Back to Players
        </Button>

        {/* Header */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-start gap-6">
              {user.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user.name}
                  className="w-24 h-24 rounded-full object-cover border-4 border-slate-200"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-bold text-3xl border-4 border-slate-300">
                  {user.name?.charAt(0)?.toUpperCase()}
                </div>
              )}
              
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-slate-800 mb-2">{user.name}</h1>
                <p className="text-slate-600 mb-3">{user.email}</p>
                
                <div className="flex gap-2 mb-3">
                  {user.role === 'admin' ? (
                    <>
                      <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-medium">Admin</span>
                      <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-sm font-medium">Player</span>
                    </>
                  ) : (
                    <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-sm font-medium">Player</span>
                  )}
                  
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    user.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                    user.status === 'invited' ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-200 text-slate-600'
                  }`}>
                    {user.status}
                  </span>
                </div>
                
                <p className="text-sm text-slate-500">Last Sign-In: {formatDate(user.last_sign_in_at)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="notifications">Notifications</TabsTrigger>
            <TabsTrigger value="role">Role & Permissions</TabsTrigger>
            <TabsTrigger value="seasons">Seasons Access</TabsTrigger>
            <TabsTrigger value="activity">Activity</TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Profile Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {/* Display Name */}
                <div className="flex items-center justify-between p-4 rounded-lg hover:bg-slate-50 transition-colors group">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Display Name</p>
                      {editingName ? (
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            className="h-8 w-48"
                            autoFocus
                          />
                          <Button size="sm" onClick={updateName} className="h-8 w-8 p-0">
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingName(false)} className="h-8 w-8 p-0">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <p className="font-medium text-slate-900">{user.name}</p>
                      )}
                    </div>
                  </div>
                  {!editingName && (
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => { setNewName(user.name); setEditingName(true); }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Player Color */}
                <div className="flex items-center justify-between p-4 rounded-lg hover:bg-slate-50 transition-colors group">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                      <Palette className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Player Color</p>
                      {editingColor ? (
                        <div className="mt-2">
                          <div className="flex flex-wrap gap-2 mb-2">
                            {PLAYER_COLORS.map((color) => (
                              <button
                                key={color.value}
                                type="button"
                                onClick={() => setNewColor(color.value)}
                                className={`w-8 h-8 rounded-full transition-all ${
                                  newColor === color.value 
                                    ? 'ring-2 ring-offset-2 ring-slate-400 scale-110' 
                                    : 'hover:scale-105'
                                }`}
                                style={{ backgroundColor: color.value }}
                                title={color.name}
                              />
                            ))}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button size="sm" onClick={updateColor} className="h-8">
                              <Check className="w-4 h-4 mr-1" /> Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingColor(false)} className="h-8">
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-6 h-6 rounded-full border-2 border-white shadow-sm" 
                            style={{ backgroundColor: user.color || '#3b82f6' }}
                          />
                          <span className="font-medium text-slate-900">
                            {getColorName(user.color)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  {!editingColor && (
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => { setNewColor(user.color || '#3b82f6'); setEditingColor(true); }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Phone Number */}
                <div className="flex items-center justify-between p-4 rounded-lg hover:bg-slate-50 transition-colors group">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Phone Number</p>
                      {editingPhone ? (
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            type="tel"
                            value={newPhone}
                            onChange={(e) => setNewPhone(formatPhoneNumber(e.target.value))}
                            placeholder="(555) 123-4567"
                            className="h-8 w-48"
                            autoFocus
                          />
                          <Button size="sm" onClick={updatePhone} className="h-8 w-8 p-0">
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingPhone(false)} className="h-8 w-8 p-0">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <p className={`font-medium ${user.phone_number ? 'text-slate-900' : 'text-slate-400 italic'}`}>
                          {user.phone_number || 'Not set'}
                        </p>
                      )}
                    </div>
                  </div>
                  {!editingPhone && (
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => { setNewPhone(user.phone_number || ''); setEditingPhone(true); }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {/* Email - Read Only */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50/50">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Email Address</p>
                      <p className="font-medium text-slate-900">{user.email}</p>
                    </div>
                  </div>
                  <span className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded">Read-only</span>
                </div>

                {/* Member Since - Read Only */}
                <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50/50">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Member Since</p>
                      <p className="font-medium text-slate-900">{formatDate(user.created_at)}</p>
                    </div>
                  </div>
                </div>

                {/* Account Status */}
                <div className="flex items-center justify-between p-4 rounded-lg border-t mt-4 pt-6">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      user.status === 'active' ? 'bg-emerald-100' : 'bg-red-100'
                    }`}>
                      <Shield className={`w-5 h-5 ${
                        user.status === 'active' ? 'text-emerald-600' : 'text-red-600'
                      }`} />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Account Status</p>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.status === 'active' 
                            ? 'bg-emerald-100 text-emerald-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {user.status === 'active' ? '● Active' : '● Deactivated'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    {user.status === 'active' ? (
                      <Dialog open={showDeactivate} onOpenChange={setShowDeactivate}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50">
                            Deactivate
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Deactivate User Account</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 mt-4">
                            <p className="text-sm text-slate-600">
                              This user will no longer be able to sign in. Historical data will be preserved.
                            </p>
                            <div>
                              <Label>Reason (Optional)</Label>
                              <Input
                                value={deactivateReason}
                                onChange={(e) => setDeactivateReason(e.target.value)}
                                placeholder="Enter reason for deactivation"
                              />
                            </div>
                            <div className="flex gap-2 justify-end">
                              <Button variant="outline" onClick={() => setShowDeactivate(false)}>Cancel</Button>
                              <Button variant="destructive" onClick={deactivateUser}>Deactivate</Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    ) : (
                      <Dialog open={reactivateDialogOpen} onOpenChange={setReactivateDialogOpen}>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" className="text-emerald-600 border-emerald-200 hover:bg-emerald-50">
                            Reactivate
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Reactivate User Account</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 mt-4">
                            <p className="text-sm text-slate-600">
                              This will restore the user&apos;s ability to sign in and access the platform.
                            </p>
                            <div className="flex gap-2 justify-end">
                              <Button variant="outline" onClick={() => setReactivateDialogOpen(false)}>Cancel</Button>
                              <Button onClick={confirmReactivate} className="bg-emerald-600 hover:bg-emerald-700">Reactivate</Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                </div>

                {/* Reset Password - Only show for non-Google auth users */}
                {!user.isInvite && (
                  <div className="flex items-center justify-between p-4 rounded-lg hover:bg-slate-50 transition-colors border-t mt-4 pt-6">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <KeyRound className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Password</p>
                        <p className="font-medium text-slate-900">
                          {user.auth_provider === 'google' ? 'Google Sign-In (no password)' : 'Email/Password'}
                        </p>
                      </div>
                    </div>
                    <Dialog open={showResetPassword} onOpenChange={setShowResetPassword}>
                      <DialogTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm"
                          disabled={user.auth_provider === 'google' && !user.password_hash}
                          data-testid="reset-password-btn"
                        >
                          <KeyRound className="w-4 h-4 mr-2" />
                          Reset Password
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Reset User Password</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <p className="text-sm text-slate-600">
                            Set a new password for <strong>{user.name}</strong>. They will be able to log in with this new password immediately.
                          </p>
                          <div>
                            <Label htmlFor="new-password">New Password</Label>
                            <Input
                              id="new-password"
                              type="password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder="Enter new password"
                              data-testid="new-password-input"
                            />
                            <p className="text-xs text-slate-500 mt-1">Minimum 8 characters</p>
                          </div>
                          <div>
                            <Label htmlFor="confirm-password">Confirm Password</Label>
                            <Input
                              id="confirm-password"
                              type="password"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              placeholder="Confirm new password"
                              data-testid="confirm-password-input"
                            />
                          </div>
                          <div className="flex gap-2 justify-end pt-2">
                            <Button 
                              variant="outline" 
                              onClick={() => {
                                setShowResetPassword(false);
                                setNewPassword('');
                                setConfirmPassword('');
                              }}
                            >
                              Cancel
                            </Button>
                            <Button 
                              onClick={resetPassword}
                              disabled={resettingPassword || !newPassword || !confirmPassword}
                              data-testid="confirm-reset-password-btn"
                            >
                              {resettingPassword ? 'Updating...' : 'Update Password'}
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* SMS Notification Settings - Only show for actual users, not invites */}
            {!user.isInvite && (
              <Card className="mt-6">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    SMS Notification Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  {/* SMS Consent Status - Shows current consent state with timestamp */}
                  <div className={`flex items-center justify-between p-4 rounded-lg ${
                    user.sms_opted_in || smsVerbalConsent 
                      ? 'bg-emerald-50/50 border border-emerald-100' 
                      : 'bg-amber-50/50 border border-amber-100'
                  }`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        user.sms_opted_in || smsVerbalConsent ? 'bg-emerald-100' : 'bg-amber-100'
                      }`}>
                        <Bell className={`w-5 h-5 ${
                          user.sms_opted_in || smsVerbalConsent ? 'text-emerald-600' : 'text-amber-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">SMS Consent Status</p>
                        {user.sms_opted_in ? (
                          <div>
                            <p className="text-sm text-emerald-700">✓ Player consented via app</p>
                            {user.sms_opted_in_at && (
                              <p className="text-xs text-slate-500 mt-0.5">
                                Consented on {formatDate(user.sms_opted_in_at)}
                              </p>
                            )}
                          </div>
                        ) : smsVerbalConsent ? (
                          <div>
                            <p className="text-sm text-emerald-700">✓ Verbal consent recorded by League Admin</p>
                            {user.sms_verbal_consent_at && (
                              <p className="text-xs text-slate-500 mt-0.5">
                                Recorded on {formatDate(user.sms_verbal_consent_at)}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-amber-700">No consent on file</p>
                        )}
                      </div>
                    </div>
                    {/* Only show verbal consent checkbox if player hasn't already consented via app */}
                    {!user.sms_opted_in && (
                      <div className="flex items-center gap-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox 
                            id="sms-consent"
                            checked={smsVerbalConsent}
                            onCheckedChange={(checked) => {
                              if (checked && !smsVerbalConsent) {
                                setShowSmsConsentDialog(true);
                              } else if (!checked) {
                                // Unchecking consent - also disable SMS
                                setSmsVerbalConsent(false);
                                authAxios.patch(
                                  `${API}/users/${userId}`,
                                  { sms_verbal_consent: false, sms_enabled: false }
                                ).then(() => {
                                  toast.success('SMS consent removed');
                                  loadUser();
                                }).catch(() => {
                                  toast.error('Failed to update');
                                });
                              }
                            }}
                          />
                          <label htmlFor="sms-consent" className="text-sm font-medium text-slate-700 cursor-pointer">
                            Verbal Consent
                          </label>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* SMS Enabled Toggle - Only when consent exists */}
                  <div className="flex items-center justify-between p-4 rounded-lg hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                        <Bell className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">SMS Notifications</p>
                        <p className="text-sm text-slate-500">
                          {!user.phone_number 
                            ? 'Phone number required' 
                            : !(user.sms_opted_in || smsVerbalConsent)
                              ? 'Consent required first' 
                              : user.sms_enabled 
                                ? 'Player will receive SMS alerts' 
                                : 'SMS notifications are disabled'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={smsSettings.sms_enabled}
                      disabled={!user.phone_number || !(user.sms_opted_in || smsVerbalConsent)}
                      onCheckedChange={(checked) => {
                        setSmsSettings({ ...smsSettings, sms_enabled: checked });
                        // Auto-save toggle
                        authAxios.patch(
                          `${API}/users/${userId}`,
                          { sms_enabled: checked },
                          { withCredentials: true }
                        ).then(() => {
                          toast.success(checked ? 'SMS notifications enabled' : 'SMS notifications disabled');
                          loadUser();
                        }).catch(() => {
                          toast.error('Failed to update setting');
                        });
                      }}
                    />
                  </div>

                  {/* Timezone */}
                  <div className="flex items-center justify-between p-4 rounded-lg hover:bg-slate-50 transition-colors group">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                        <Globe className="w-5 h-5 text-slate-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-slate-500">Timezone</p>
                        {editingSmsSettings ? (
                          <Select
                            value={smsSettings.timezone}
                            onValueChange={(value) => setSmsSettings({ ...smsSettings, timezone: value })}
                          >
                            <SelectTrigger className="w-64 mt-1">
                              <SelectValue placeholder="Select timezone" />
                            </SelectTrigger>
                            <SelectContent>
                              {TIMEZONE_OPTIONS.map(tz => (
                                <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className={`font-medium ${user.timezone ? 'text-slate-900' : 'text-slate-400 italic'}`}>
                            {TIMEZONE_OPTIONS.find(tz => tz.value === user.timezone)?.label || 'Not set (uses league default)'}
                          </p>
                        )}
                      </div>
                    </div>
                    {!editingSmsSettings && (
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => setEditingSmsSettings(true)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  {/* Quiet Hours */}
                  <div className="flex items-center justify-between p-4 rounded-lg hover:bg-slate-50 transition-colors group">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-slate-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-slate-500">Quiet Hours (No SMS during these hours)</p>
                        {editingSmsSettings ? (
                          <div className="flex items-center gap-2 mt-1">
                            <Select
                              value={smsSettings.quiet_hours_start}
                              onValueChange={(value) => setSmsSettings({ ...smsSettings, quiet_hours_start: value })}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {HOUR_OPTIONS.map(h => (
                                  <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <span className="text-slate-500">to</span>
                            <Select
                              value={smsSettings.quiet_hours_end}
                              onValueChange={(value) => setSmsSettings({ ...smsSettings, quiet_hours_end: value })}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {HOUR_OPTIONS.map(h => (
                                  <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <p className="font-medium text-slate-900">
                            {HOUR_OPTIONS.find(h => h.value === user.quiet_hours_start)?.label || '8:00 PM'} to {HOUR_OPTIONS.find(h => h.value === user.quiet_hours_end)?.label || '7:00 AM'}
                          </p>
                        )}
                      </div>
                    </div>
                    {!editingSmsSettings && (
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => setEditingSmsSettings(true)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  {/* Save/Cancel for SMS Settings */}
                  {editingSmsSettings && (
                    <div className="flex justify-end gap-2 pt-4 border-t mt-4">
                      <Button variant="outline" onClick={() => {
                        setEditingSmsSettings(false);
                        // Reset to user values
                        setSmsSettings({
                          sms_enabled: user.sms_enabled || false,
                          timezone: user.timezone || '',
                          quiet_hours_start: user.quiet_hours_start || '20:00',
                          quiet_hours_end: user.quiet_hours_end || '07:00'
                        });
                      }}>
                        Cancel
                      </Button>
                      <Button onClick={updateSmsSettings}>
                        <Check className="w-4 h-4 mr-2" />
                        Save SMS Settings
                      </Button>
                    </div>
                  )}

                  {/* Info note */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mt-4">
                    <p className="text-sm text-slate-700">
                      <Bell className="w-4 h-4 inline mr-1" />
                      SMS notifications require a phone number and consent (either from the player directly or recorded verbal consent). Messages will not be sent during quiet hours.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Notifications Tab - Individual Email/SMS preferences */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Individual Notification Preferences
                </CardTitle>
                <p className="text-sm text-slate-600 mt-1">
                  Manage which email and SMS notifications this player receives
                </p>
              </CardHeader>
              <CardContent>
                {user.isInvite ? (
                  <div className="text-center py-8 text-slate-500">
                    <Bell className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p>Notification preferences are available after the player accepts their invite.</p>
                  </div>
                ) : (
                  <EmailPreferences userId={userId} showHeader={false} />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Role & Permissions Tab */}
          <TabsContent value="role">
            <Card>
              <CardHeader>
                <CardTitle>Role & Permissions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Roles</Label>
                  <div className="mt-4 space-y-4">
                    {/* Player Role */}
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={true}
                        disabled={true}
                        className="w-5 h-5 text-amber-600 rounded focus:ring-amber-500 mt-0.5 cursor-not-allowed opacity-50"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-slate-800">Player</div>
                        <p className="text-sm text-slate-600">
                          Can draft contestants, view scores, and manage their own profile
                        </p>
                      </div>
                    </label>

                    {/* Admin Role */}
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={user.role === 'admin'}
                        onChange={(e) => handleRoleChangeClick(e.target.checked ? 'admin' : 'player')}
                        className="w-5 h-5 text-amber-600 rounded focus:ring-amber-500 mt-0.5"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-slate-800">Admin</div>
                        <p className="text-sm text-slate-600">
                          Full access to manage all aspects of the league including contestants, scoring rules, and players
                        </p>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="font-semibold text-amber-900 mb-2">Note:</h4>
                  <p className="text-sm text-amber-800">
                    All users are Players by default. There must always be at least one Admin in the league. You cannot demote the last Admin.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Seasons Access Tab */}
          <TabsContent value="seasons">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Seasons Access</CardTitle>
                  <Button size="sm" onClick={handleGrantAllAccessClick}>
                    Add to All Seasons
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {seasons.length === 0 ? (
                    <p className="text-slate-600">No seasons available</p>
                  ) : (
                    seasons.map(season => {
                      const hasAccess = user.season_ids?.includes(season.id);
                      return (
                        <div key={season.id} className="flex items-center justify-between p-4 border rounded-lg">
                          <div>
                            <h4 className="font-semibold">Season {season.number}</h4>
                            {season.title && <p className="text-sm text-slate-600">{season.title}</p>}
                            <p className="text-xs text-slate-500 capitalize">{season.status}</p>
                          </div>
                          <Button
                            variant={hasAccess ? 'destructive' : 'default'}
                            size="sm"
                            onClick={() => toggleSeason(season.id)}
                          >
                            {hasAccess ? 'Remove' : 'Add'}
                          </Button>
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity">
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                {user.recent_activity?.length === 0 ? (
                  <p className="text-slate-600 text-center py-8">No activity recorded yet</p>
                ) : (
                  <div className="space-y-3">
                    {user.recent_activity?.map(event => (
                      <div key={event.id} className="border-l-4 border-blue-500 pl-4 py-2">
                        <p className="font-medium">{getActionDescription(event.action_type)}</p>
                        <p className="text-sm text-slate-600">{formatDate(event.created_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Role Change Confirmation Dialog */}
      <Dialog open={roleChangeDialogOpen} onOpenChange={setRoleChangeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>
              Change role to <strong>{newRoleToSet}</strong>?
            </p>
            <p className="text-sm text-slate-600">
              {newRoleToSet === 'admin' 
                ? 'This will grant full administrative access to all features and data.'
                : 'This will remove administrative privileges and restrict access to player features only.'}
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setRoleChangeDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={confirmRoleChange}>
                Change Role
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Grant All Access Confirmation Dialog */}
      <Dialog open={grantAllAccessDialogOpen} onOpenChange={setGrantAllAccessDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Grant Access to All Seasons</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>
              Grant <strong>{user?.name}</strong> access to all seasons?
            </p>
            <p className="text-sm text-slate-600">
              This will add this user to all {seasons.length} season(s) at once.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setGrantAllAccessDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={confirmGrantAllAccess}>
                Grant All Access
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reactivate User Confirmation Dialog */}
      <Dialog open={reactivateDialogOpen} onOpenChange={setReactivateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reactivate User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>
              Reactivate <strong>{user?.name}</strong>?
            </p>
            <p className="text-sm text-slate-600">
              This will restore full access to the account and all associated seasons.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setReactivateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={confirmReactivate}>
                Reactivate User
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* SMS Verbal Consent Confirmation Dialog */}
      <Dialog open={showSmsConsentDialog} onOpenChange={setShowSmsConsentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm SMS Verbal Consent</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800 font-medium mb-2">
                By checking this box, you confirm that:
              </p>
              <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                <li><strong>{user?.name}</strong> has verbally consented to receive SMS notifications from this league</li>
                <li>You have explained what types of messages they will receive</li>
                <li>They understand they can opt out at any time</li>
              </ul>
            </div>
            <p className="text-sm text-slate-600">
              This consent record will be stored for compliance purposes. SMS notifications will be automatically enabled.
            </p>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowSmsConsentDialog(false)}>
                Cancel
              </Button>
              <Button onClick={() => {
                setSmsVerbalConsent(true);
                setSmsSettings({ ...smsSettings, sms_enabled: true });
                authAxios.patch(
                  `${API}/users/${userId}`,
                  { 
                    sms_verbal_consent: true, 
                    sms_verbal_consent_by: currentUser?.id, 
                    sms_verbal_consent_at: new Date().toISOString(),
                    sms_enabled: true  // Auto-enable SMS when consent is recorded
                  }
                ).then(() => {
                  toast.success('SMS consent recorded and notifications enabled');
                  setShowSmsConsentDialog(false);
                  loadUser();
                }).catch(() => {
                  toast.error('Failed to save consent');
                });
              }}>
                Confirm Consent & Enable SMS
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
